import { useState, useEffect } from "react";
import { 
  FileText, DollarSign, Clock, CheckCircle, XCircle, 
  Download, Send, Filter, Search, Plus, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiClient } from "@/App";

const CLAIM_TYPES = [
  { value: "medicaid", label: "Medicaid", color: "bg-blue-500" },
  { value: "medicare", label: "Medicare", color: "bg-green-500" },
  { value: "private_insurance", label: "Private Insurance", color: "bg-purple-500" }
];

const CLAIM_STATUSES = [
  { value: "pending", label: "Pending", color: "bg-amber-500", icon: Clock },
  { value: "submitted", label: "Submitted", color: "bg-blue-500", icon: Send },
  { value: "approved", label: "Approved", color: "bg-green-500", icon: CheckCircle },
  { value: "denied", label: "Denied", color: "bg-red-500", icon: XCircle },
  { value: "paid", label: "Paid", color: "bg-emerald-500", icon: DollarSign }
];

export default function InsuranceClaimsManager({ completedRides = [] }) {
  const [claims, setClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [newClaim, setNewClaim] = useState({
    claim_type: "medicaid",
    provider_name: "",
    member_id: "",
    authorization_number: "",
    notes: ""
  });

  useEffect(() => {
    fetchClaims();
  }, [filterStatus]);

  const fetchClaims = async () => {
    try {
      const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const response = await apiClient.get(`/claims${params}`);
      setClaims(response.data);
    } catch (error) {
      console.error("Failed to fetch claims:", error);
    }
  };

  const handleCreateClaim = async () => {
    if (!selectedRide) {
      toast.error("Please select a ride");
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post("/claims", {
        ride_id: selectedRide.id,
        ...newClaim
      });
      toast.success("Claim created successfully!");
      setShowCreateDialog(false);
      setSelectedRide(null);
      setNewClaim({
        claim_type: "medicaid",
        provider_name: "",
        member_id: "",
        authorization_number: "",
        notes: ""
      });
      fetchClaims();
    } catch (error) {
      toast.error("Failed to create claim");
    } finally {
      setIsLoading(false);
    }
  };

  const updateClaimStatus = async (claimId, status) => {
    try {
      await apiClient.put(`/claims/${claimId}?status=${status}`);
      toast.success(`Claim ${status}`);
      fetchClaims();
    } catch (error) {
      toast.error("Failed to update claim");
    }
  };

  const exportClaims = async () => {
    try {
      const response = await apiClient.get("/claims/export");
      const data = response.data;
      
      // Convert to CSV
      if (data.data.length === 0) {
        toast.error("No claims to export");
        return;
      }
      
      const headers = Object.keys(data.data[0]);
      const csvContent = [
        headers.join(","),
        ...data.data.map(row => headers.map(h => `"${row[h] || ''}"`).join(","))
      ].join("\n");
      
      // Download
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${data.record_count} claims`);
    } catch (error) {
      toast.error("Failed to export claims");
    }
  };

  const getStatusInfo = (status) => {
    return CLAIM_STATUSES.find(s => s.value === status) || CLAIM_STATUSES[0];
  };

  const getClaimTypeInfo = (type) => {
    return CLAIM_TYPES.find(t => t.value === type) || CLAIM_TYPES[0];
  };

  const filteredClaims = claims.filter(claim => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        claim.ride_details?.client_name?.toLowerCase().includes(search) ||
        claim.member_id?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === "pending").length,
    submitted: claims.filter(c => c.status === "submitted").length,
    approved: claims.filter(c => c.status === "approved" || c.status === "paid").length,
    totalAmount: claims.filter(c => c.amount).reduce((sum, c) => sum + (c.amount || 0), 0)
  };

  return (
    <div className="space-y-4" data-testid="insurance-claims-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-500" />
          <h3 className="font-bold text-lg">Insurance Claims</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportClaims}>
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-1" />
                New Claim
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Insurance Claim</DialogTitle>
                <DialogDescription>
                  Create a claim for a completed ride
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Ride Selection */}
                <div>
                  <Label>Select Completed Ride</Label>
                  <Select 
                    value={selectedRide?.id || ""}
                    onValueChange={(id) => setSelectedRide(completedRides.find(r => r.id === id))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a ride" />
                    </SelectTrigger>
                    <SelectContent>
                      {completedRides.map(ride => (
                        <SelectItem key={ride.id} value={ride.id}>
                          {ride.client_name} - {ride.pickup_date} ({ride.dropoff_address})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Claim Type */}
                <div>
                  <Label>Claim Type</Label>
                  <Select 
                    value={newClaim.claim_type}
                    onValueChange={(v) => setNewClaim({...newClaim, claim_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLAIM_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Provider & Member Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Provider Name</Label>
                    <Input 
                      value={newClaim.provider_name}
                      onChange={(e) => setNewClaim({...newClaim, provider_name: e.target.value})}
                      placeholder="Insurance provider"
                    />
                  </div>
                  <div>
                    <Label>Member ID</Label>
                    <Input 
                      value={newClaim.member_id}
                      onChange={(e) => setNewClaim({...newClaim, member_id: e.target.value})}
                      placeholder="Member/Policy ID"
                    />
                  </div>
                </div>

                {/* Authorization */}
                <div>
                  <Label>Authorization Number (if applicable)</Label>
                  <Input 
                    value={newClaim.authorization_number}
                    onChange={(e) => setNewClaim({...newClaim, authorization_number: e.target.value})}
                    placeholder="Prior authorization #"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Input 
                    value={newClaim.notes}
                    onChange={(e) => setNewClaim({...newClaim, notes: e.target.value})}
                    placeholder="Additional information"
                  />
                </div>

                <Button 
                  onClick={handleCreateClaim}
                  disabled={isLoading || !selectedRide}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  {isLoading ? "Creating..." : "Create Claim"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="border-0 bg-slate-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Claims</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-amber-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
            <p className="text-xs text-amber-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-blue-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.submitted}</p>
            <p className="text-xs text-blue-600">Submitted</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-green-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-700">${stats.totalAmount.toLocaleString()}</p>
            <p className="text-xs text-green-600">Approved/Paid</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search by client or member ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {CLAIM_STATUSES.map(status => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Claims List */}
      <div className="space-y-3">
        {filteredClaims.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No claims found</p>
          </div>
        ) : (
          filteredClaims.map((claim) => {
            const statusInfo = getStatusInfo(claim.status);
            const typeInfo = getClaimTypeInfo(claim.claim_type);
            const StatusIcon = statusInfo.icon;
            
            return (
              <Card key={claim.id} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                        <Badge className={`${statusInfo.color} flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </Badge>
                        {claim.amount && (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            ${claim.amount.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <p className="font-semibold">{claim.ride_details?.client_name}</p>
                        <p className="text-slate-600">
                          {claim.ride_details?.pickup_date} • {claim.ride_details?.pickup_address} → {claim.ride_details?.dropoff_address}
                        </p>
                        {claim.member_id && (
                          <p className="text-slate-500">Member ID: {claim.member_id}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {claim.status === "pending" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateClaimStatus(claim.id, "submitted")}
                          className="text-blue-600 border-blue-300"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Submit
                        </Button>
                      )}
                      {claim.status === "submitted" && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateClaimStatus(claim.id, "approved")}
                            className="text-green-600 border-green-300"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateClaimStatus(claim.id, "denied")}
                            className="text-red-600 border-red-300"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {claim.status === "approved" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateClaimStatus(claim.id, "paid")}
                          className="text-emerald-600 border-emerald-300"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
